import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import useAllTimelocks, { TimelockItem } from '../hooks/useAllTimelocks'
import Network from './general/Network'

const TimelocksList: React.FC = () => {
  const { data: timelocks, isLoading } = useAllTimelocks()
  const [currentTime, setCurrentTime] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 60000)
    return () => clearInterval(interval)
  }, [])

  const calculateHoursRemaining = (allowedAtTimestamp: string) => {
    const diff = parseInt(allowedAtTimestamp) - Math.floor(currentTime / 1000)
    if (diff <= 0) return `-${Math.abs(Math.floor(diff / 3600))}h`
    return `${Math.floor(diff / 3600)}h`
  }

  const isPastTimelock = (allowedAtTimestamp: string) =>
    parseInt(allowedAtTimestamp) < Math.floor(currentTime / 1000)

  const formatDate = (timestamp: string) =>
    new Date(parseInt(timestamp) * 1000).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  if (isLoading) {
    return (
      <Container>
        <Header>
          <SectionTitle>Upcoming Timelocks</SectionTitle>
        </Header>
        <TableWrap>
          <LoaderBar />
        </TableWrap>
      </Container>
    )
  }

  if (!timelocks || timelocks.length === 0) {
    return (
      <Container>
        <Header>
          <SectionTitle>Upcoming Timelocks</SectionTitle>
        </Header>
        <EmptyState>No upcoming timelocks</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <SectionTitle>Upcoming Timelocks</SectionTitle>
        <SectionCount>{timelocks.length} next executions</SectionCount>
      </Header>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Environment</th>
              <th>Task</th>
              <th>Chain</th>
              <th className="right">Scheduled</th>
            </tr>
          </thead>
          <tbody>
            {timelocks.map((timelock, index) => {
              const chains = timelock.chainIds || (timelock.chainId ? [timelock.chainId] : [])
              const isPast = isPastTimelock(timelock.timelock.allowedAt)
              return (
                <tr
                  key={`${timelock.environment}-${timelock.task}-${timelock.timelock.allowedAt}-${index}`}
                  className={isPast ? 'past' : ''}
                >
                  <td className="environment">{timelock.environment}</td>
                  <td className="task">{timelock.task}</td>
                  <td className="chain">
                    <ChainIcons>
                      {chains.map((chainId, idx) => (
                        <span key={`${chainId}-${idx}`}>
                          <Network network={chainId} small={true} />
                        </span>
                      ))}
                    </ChainIcons>
                  </td>
                  <td className="scheduled">
                    <ScheduledCell>
                      <ScheduledDate past={isPast}>{formatDate(timelock.timelock.allowedAt)}</ScheduledDate>
                      <Countdown past={isPast}>{calculateHoursRemaining(timelock.timelock.allowedAt)}</Countdown>
                    </ScheduledCell>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Container>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Container = styled.div`
  width: 100%;
  margin-bottom: 28px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

const SectionTitle = styled.span`
  color: #777;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`

const SectionCount = styled.span`
  color: #777;
  font-size: 12px;
`

const TableWrap = styled.div`
  background: #111;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  thead th {
    padding: 8px 16px;
    text-align: left;
    color: #777;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;

    &.right { text-align: right; }
  }

  tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    transition: background 0.1s;

    &:last-child { border-bottom: none; }

    &:hover { background: rgba(255, 255, 255, 0.02); }

    &.past {
      background: rgba(255, 204, 51, 0.03);
      &:hover { background: rgba(255, 204, 51, 0.05); }
    }
  }

  tbody td {
    padding: 9px 16px;
    font-size: 13px;

    &.environment {
      color: #ededed;
      font-weight: 500;
    }

    &.task {
      color: #777;
      font-family: 'SF Mono', 'Fira Code', monospace;
      font-size: 12px;
    }

    &.chain { min-width: 80px; }

    &.scheduled { text-align: right; }
  }
`

const ChainIcons = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
  flex-wrap: wrap;

  span { display: inline-flex; }
  img { margin-right: 0 !important; }
`

const ScheduledCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`

const ScheduledDate = styled.span<{ past: boolean }>`
  color: ${({ past }) => past ? '#FFCC33' : '#a0a0a0'};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`

const Countdown = styled.span<{ past: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'SF Mono', 'Fira Code', monospace;

  ${({ past }) => past ? `
    background: rgba(255, 204, 51, 0.1);
    color: #FFCC33;
    border: 1px solid rgba(255, 204, 51, 0.2);
  ` : `
    background: rgba(111, 92, 230, 0.1);
    color: #a89aff;
    border: 1px solid rgba(111, 92, 230, 0.2);
  `}
`

const loadingAnim = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
`

const LoaderBar = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 25%;
    height: 100%;
    background: linear-gradient(90deg, transparent, #6f5ce6, transparent);
    animation: ${loadingAnim} 1.2s ease-in-out infinite;
  }
`

const EmptyState = styled.div`
  padding: 24px 16px;
  color: #777;
  font-size: 13px;
  text-align: center;
  background: #111;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.07);
`

export default TimelocksList
